// Kore Wellbeing analytics helper.
//
// One place for GA4 initialisation, hostname gating, deduped page_view firing,
// UTM persistence and every conversion event the marketing brief specifies.
//
// Design rules:
//   - GA4 is initialised ONLY on production hostnames (korewellbeing.com,
//     www.korewellbeing.com). Preview/staging/dev domains are silent.
//   - `send_page_view: false` is passed to gtag('config') so the initial hard
//     load does not double-fire. The router calls trackPageView() for both the
//     initial load and every subsequent client-side route change.
//   - Every event is deduped per user action via a small in-memory Set keyed on
//     "eventName::stableActionKey" that clears on route change.
//   - No PII (name/email/phone) is ever sent to GA4. Params are stripped.
//   - First-touch and current UTMs are persisted in sessionStorage and can be
//     appended to outbound URLs (TeamUp, Calendly, booking).

const MEASUREMENT_ID = "G-THQYG29ZST";
const PROD_HOSTS = new Set(["korewellbeing.com", "www.korewellbeing.com"]);
const EMERGENT_REFERRER_TOKEN = "app.emergent.sh";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
const PII_KEYS = new Set([
  "name", "first_name", "last_name", "email", "email_address", "phone",
  "mobile", "tel", "user_email", "user_name",
]);

const state = {
  ready: false,
  loaded: false,
  lastPageKey: null,
  firedActions: new Set(),
};

// ----------------- ENVIRONMENT / GATING -----------------

export function isProdHost() {
  if (typeof window === "undefined") return false;
  return PROD_HOSTS.has(window.location.hostname);
}

function isEmergentReferralSession() {
  if (typeof document === "undefined") return false;
  try {
    return (document.referrer || "").includes(EMERGENT_REFERRER_TOKEN);
  } catch { return false; }
}

// ----------------- GA4 LOADER -----------------

export function initAnalytics() {
  if (state.ready) return;
  if (typeof window === "undefined") return;
  if (!isProdHost()) return;                 // do NOT load on preview / localhost
  if (isEmergentReferralSession()) return;    // exclude Emergent-referred sessions

  // Inject the gtag script exactly once.
  if (!state.loaded) {
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
    document.head.appendChild(s);
    state.loaded = true;
  }
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = window.gtag || gtag;
  window.gtag("js", new Date());
  // send_page_view:false → we fire page_view manually from the router so
  // the initial hard load never double-counts.
  window.gtag("config", MEASUREMENT_ID, { send_page_view: false });
  state.ready = true;

  // Persist first-touch UTMs on the very first page landed.
  captureUTMs();
}

// ----------------- UTM PERSISTENCE -----------------

function readSession(key) {
  try { return JSON.parse(sessionStorage.getItem(key) || "null"); } catch { return null; }
}
function writeSession(key, value) {
  try { sessionStorage.setItem(key, JSON.stringify(value)); } catch { /* quota */ }
}

function captureUTMs() {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  const current = {};
  UTM_KEYS.forEach((k) => { const v = params.get(k); if (v) current[k] = v; });
  if (Object.keys(current).length) {
    writeSession("kore_utms_current", current);
    if (!readSession("kore_utms_first")) writeSession("kore_utms_first", current);
  }
}

export function getUTMs() {
  if (typeof window === "undefined") return {};
  return {
    first: readSession("kore_utms_first") || {},
    current: readSession("kore_utms_current") || {},
  };
}

export function appendUTMs(url) {
  try {
    const u = new URL(url, typeof window !== "undefined" ? window.location.origin : undefined);
    const { current } = getUTMs();
    Object.entries(current).forEach(([k, v]) => { if (!u.searchParams.has(k)) u.searchParams.set(k, v); });
    return u.toString();
  } catch { return url; }
}

// ----------------- CORE FIRING -----------------

function stripPII(params) {
  const out = {};
  Object.entries(params || {}).forEach(([k, v]) => {
    if (PII_KEYS.has(k)) return;
    if (v === undefined || v === null) return;
    out[k] = v;
  });
  return out;
}

function fireEvent(name, params, { dedupeKey } = {}) {
  if (!state.ready) return;
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  if (dedupeKey) {
    const key = `${name}::${dedupeKey}`;
    if (state.firedActions.has(key)) return;
    state.firedActions.add(key);
  }
  window.gtag("event", name, stripPII(params));
}

// ----------------- PUBLIC EVENT API -----------------

export function trackPageView({ path, location, title } = {}) {
  if (!state.ready) return;
  const key = path || location || "";
  if (!key) return;                          // never fire with a blank page_path
  if (state.lastPageKey === key) return;      // dedupe against identical URL
  state.lastPageKey = key;
  state.firedActions.clear();                 // reset per-route action guards

  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", "page_view", {
      page_path: path,
      page_location: location,
      page_title: title,
    });
  }
  // Refresh the current UTMs if a new route carries them.
  captureUTMs();
}

// Homepage pathway cards ("Which sounds more like you?")
export function trackPathway({ pathway, cta_text, destination_url }) {
  const page_path = typeof window !== "undefined" ? window.location.pathname : "";
  fireEvent("membership_pathway_click",
    { pathway, page_path, cta_text, destination_url },
    { dedupeKey: `${page_path}|${pathway}` });
}

// Any primary/secondary/sticky CTA click
export function trackCta({ cta_name, cta_text, destination_url, cta_location = "body" }) {
  const page_path = typeof window !== "undefined" ? window.location.pathname : "";
  fireEvent("cta_click",
    { cta_name, cta_text, page_path, destination_url, cta_location },
    { dedupeKey: `${page_path}|${cta_name}|${cta_location}` });
}

// Fires the moment a form is first interacted with (first focus/change)
export function trackFormStart({ form_name }) {
  const page_path = typeof window !== "undefined" ? window.location.pathname : "";
  fireEvent("form_start", { form_name, page_path }, { dedupeKey: `${page_path}|${form_name}` });
}

// Fires ONLY after a genuine successful enquiry-form submission
export function trackLead({ form_name, enquiry_type }) {
  const page_path = typeof window !== "undefined" ? window.location.pathname : "";
  fireEvent("generate_lead",
    { form_name, enquiry_type, page_path },
    { dedupeKey: `${page_path}|${form_name}|lead` });
}

// Fires only when a confirmed consultation booking is captured
export function trackConsultation({ booking_type }) {
  const page_path = typeof window !== "undefined" ? window.location.pathname : "";
  fireEvent("book_consultation",
    { booking_type, page_path },
    { dedupeKey: `${page_path}|${booking_type}|consult` });
}

// Fires once when someone proceeds towards an external trial / membership checkout
export function trackBeginCheckout({ item_name, membership_type, value, currency = "GBP", destination_url }) {
  fireEvent("begin_checkout",
    { item_name, membership_type, value, currency, destination_url },
    { dedupeKey: `${item_name}|${membership_type}|checkout` });
}

// Fires only when a genuine purchase success can be confirmed
export function trackPurchase({ transaction_id, value, currency = "GBP", item_name, membership_type }) {
  if (!transaction_id) return;
  fireEvent("purchase",
    { transaction_id, value, currency, item_name, membership_type },
    { dedupeKey: `purchase|${transaction_id}` });
}

// ----------------- 6-WEEK RESET FUNNEL EVENTS -----------------
// Named per brand brief. Each is deduped so the same visitor firing the same
// action multiple times only counts once per route (or once per session for
// page_view and once per transaction for payment events).

// Fires exactly once on landing / navigating to the 6-Week Reset landing page.
export function trackResetPageView() {
  const page_path = typeof window !== "undefined" ? window.location.pathname : "";
  fireEvent("reset_page_view", { page_path }, { dedupeKey: `${page_path}|reset_page_view` });
}

// Any Reset-focused CTA on the Reset landing page or elsewhere.
export function trackResetCtaClick({ cta_text, destination_url, cta_location = "body" }) {
  const page_path = typeof window !== "undefined" ? window.location.pathname : "";
  fireEvent("reset_cta_click",
    { cta_text, destination_url, cta_location, page_path },
    { dedupeKey: `${page_path}|${cta_text}|${cta_location}|reset_cta` });
}

// Fires when the visitor first interacts with the Reset application form.
export function trackResetFormStart() {
  const page_path = typeof window !== "undefined" ? window.location.pathname : "";
  fireEvent("reset_form_start", { form_name: "6_week_reset_application", page_path },
    { dedupeKey: `${page_path}|reset_form_start` });
}

// Fires ONLY after a genuine successful Reset application submit.
export function trackResetFormSubmit() {
  const page_path = typeof window !== "undefined" ? window.location.pathname : "";
  fireEvent("reset_form_submit", { form_name: "6_week_reset_application", page_path },
    { dedupeKey: `${page_path}|reset_form_submit` });
}

// Fires when the Calendly consultation embed is presented to the user.
export function trackConsultationBookingStart({ booking_type = "trial_consultation" } = {}) {
  const page_path = typeof window !== "undefined" ? window.location.pathname : "";
  fireEvent("consultation_booking_start", { booking_type, page_path },
    { dedupeKey: `${page_path}|${booking_type}|consult_start` });
}

// Fires when a Calendly booking is confirmed (event_scheduled postMessage).
export function trackConsultationBookingComplete({ booking_type = "trial_consultation" } = {}) {
  const page_path = typeof window !== "undefined" ? window.location.pathname : "";
  fireEvent("consultation_booking_complete", { booking_type, page_path },
    { dedupeKey: `${page_path}|${booking_type}|consult_complete` });
}

// Reserved for future warm-traffic direct-pay flow. Fires when payment intent
// is initiated (Stripe checkout session created).
export function trackResetPaymentStarted({ value, currency = "GBP", plan = "full" } = {}) {
  fireEvent("reset_payment_started", { value, currency, plan, item_name: "6-Week Reset" },
    { dedupeKey: `reset_payment_started|${plan}` });
}

// Reserved for future direct-pay flow. Fires when Stripe confirms success.
export function trackResetPaymentComplete({ transaction_id, value, currency = "GBP", plan = "full" }) {
  if (!transaction_id) return;
  fireEvent("reset_payment_complete",
    { transaction_id, value, currency, plan, item_name: "6-Week Reset" },
    { dedupeKey: `reset_payment_complete|${transaction_id}` });
}
